import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Star, Globe, MapPin, User, ArrowLeft
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SharedCollection = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCollection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/collections/by-code/${shareCode}`);
      setCollection(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || "Collection nicht gefunden");
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Star className="w-16 h-16 text-muted-foreground mb-6" />
        <h1 className="text-2xl font-bold mb-2">Collection nicht gefunden</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zur Startseite
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>
            
            <Button onClick={() => navigate('/')} variant="outline" size="sm">
              Jetzt beitreten
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-6xl mb-4 block">{collection.emoji}</span>
          <h1 className="text-3xl font-bold tracking-tight mb-2">{collection.name}</h1>
          {collection.description && (
            <p className="text-muted-foreground mb-4 max-w-xl mx-auto">{collection.description}</p>
          )}
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            {collection.owner && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                von {collection.owner.name}
                {collection.owner.badge && (
                  <Badge variant="outline" className="ml-1 text-xs">{collection.owner.badge}</Badge>
                )}
              </span>
            )}
            <Badge variant="outline">
              <Globe className="w-3 h-3 mr-1" />
              {collection.restaurant_count} Restaurants
            </Badge>
          </div>
        </div>

        {/* Restaurants List */}
        {collection.restaurants?.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Diese Collection ist noch leer</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {collection.restaurants?.map((restaurant) => (
              <Card 
                key={restaurant.restaurant_id}
                className="bg-card border-border"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex gap-0.5">
                      {[...Array(restaurant.stars || 1)].map((_, i) => (
                        <Star key={`shared-star-${restaurant.restaurant_id}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                      ))}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{restaurant.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {restaurant.city}, {restaurant.country}
                      </p>
                    </div>
                    {restaurant.cuisine && (
                      <Badge variant="outline" className="text-xs">
                        {restaurant.cuisine}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="py-8">
              <h2 className="text-xl font-bold mb-2">Erstelle deine eigenen Collections!</h2>
              <p className="text-muted-foreground mb-4">
                Verfolge deine Michelin-Sterne-Reise mit Étoile
              </p>
              <Button onClick={() => navigate('/')} className="bg-primary text-primary-foreground">
                Kostenlos starten
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SharedCollection;
